import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import _ from "lodash";
import { MAIN_MENU_URLS } from "../../_utils/constants/menuOrder";
import { USER_MENU_LIST_REQUEST } from "../../_utils/constants/admin/Admin";

// Normalize a url for comparison: trim, drop a trailing slash and lowercase so
// casing differences between the route path and the api menuUrl never matter.
const normalize = (url = "") => url.trim().replace(/\/+$/, "").toLowerCase();

const MAIN_MENU_SET = new Set(MAIN_MENU_URLS.map(normalize));

// Restricts direct-URL access to MAIN MENU pages the logged-in user is not
// permitted to see. Renders nothing for every other route, so it never affects
// detail pages, admin value-lists or any non main-menu route.
const MenuPermissionGuard = ({
  auth: { auth, isAuthenticated },
  Admin: { userMenuList },
  fetchUserMenuList,
}) => {
  const location = useLocation();
  const currentPath = normalize(location.pathname);

  // Safety net: make sure the permission list is loaded even if the Sidebar
  // that normally fetches it has not mounted yet on a direct URL hit.
  useEffect(() => {
    if (
      isAuthenticated &&
      _.isEmpty(userMenuList) &&
      auth[0]?.token &&
      auth[0]?.roleid &&
      auth[0]?.userId
    ) {
      fetchUserMenuList(auth[0].token, auth[0].roleid, auth[0].userId);
    }
  }, [isAuthenticated, userMenuList, auth]);

  if (!isAuthenticated) {
    return null;
  }

  const isAdmin = auth[0]?.roleName === "Admin";

  // Admin section: any /admin sub-page that is not itself a governed main-menu
  // url (e.g. /admin/product-catalog/store-list) is visible to the Admin role only.
  const isAdminRoute =
    currentPath === "/admin" || currentPath.startsWith("/admin/");
  if (isAdminRoute && !MAIN_MENU_SET.has(currentPath) && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Only guard main menu urls; everything else passes through untouched.
  if (!MAIN_MENU_SET.has(currentPath)) {
    return null;
  }

  // Wait until permissions are available before deciding anything.
  if (_.isEmpty(userMenuList) || !userMenuList[0]?.permission) {
    return null;
  }

  const allowedUrls = userMenuList[0].permission
    .filter((item) => item?.status)
    .map((item) => normalize(item?.menuUrl));
  if (!allowedUrls.includes(currentPath) && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return null;
};

const mapStateToProps = ({ auth, Admin }) => ({ auth, Admin });

const mapDispatchToProps = (dispatch) => ({
  fetchUserMenuList: (token, roleId, userId) =>
    dispatch({ type: USER_MENU_LIST_REQUEST, token, roleId, userId }),
});

export default connect(mapStateToProps, mapDispatchToProps)(MenuPermissionGuard);
